import { Breadcrumbs } from "@/ui/molecules/Breadcrumbs";
import { Link } from "./components/Link";
import { PageHeading } from "./components/Pageheading";
import clsx from "clsx";
import { DynamicLogo } from "@/features/Branding";
import { Spinner } from "@/ui/atoms/Spinner";
import { useMemoText } from "./hooks/useMemoText";
import { MemoTextJsonTypeEnum } from "@/entities/MemoText/MemoText.model";
import { ExpandableMarkdown } from "./components/ExpandableMarkdown";
import { MetaInfoBar } from "./components/MetaInfoBar";
import { useAtom, useAtomValue } from "jotai";
import {
    rightDrawerOpenAtom,
    RightDrawerSize,
} from "@/features/Navigation/state";
import { useApiQuery } from "@/api";
import { getCompany } from "@/entities/Company";
import { getSFDCData } from "@/entities/Deal";
import { ArrowPathIcon } from "@heroicons/react/24/outline";
import TypewriterEffect from "./components/TypewriterEffect";
import { motion } from "framer-motion";
import { RightDrawerContent } from "@/features/Navigation/components/RightDrawerContent";
import { FinanceLabMetricTable } from "./components/FinanceLabMetricTable";
import { LineagePanel } from "./components/LineagePanel";
import { DealStructure } from "./components/DealStructure";
import {
    ANIMATION_DURATION,
    SLIDE_TRANSITION,
} from "@/features/Navigation/constants/animations";
import { DataSourcePanel } from "./components/DataSourcePanel";
import { FinancialHighlights } from "./components/FinancialHighlights";
import { DueDiligence } from "./components/DueDiligence";
import { TransactionSummary } from "./components/TransactionSummary";
import { InvestmentRationale } from "./components/InvestmentRationale";
import { KeyRisks } from "./components/KeyRisks";
import { FundingAndValuation } from "./components/FundingAndValuation";
import { IndustryOverview } from "./components/IndustryOverview";
import { General } from "./components/General";
import { formattedUrl } from "@/utils/misc/formattedUrl";
import {
    selectedMetricForDrawerAtom,
    selectedMetricForLineageAtom,
    showInvestmentCriteriaDrawerAtom,
} from "./state";
import { InvestmentCriteriaDrawer } from "./components/InvestmentCriteriaDrawer";
import { MemoCollapse } from "./components/MemoCollapse";
import { SummaryFinancials } from "./components/SummaryFinancials";
import { SummaryProjections } from "./components/SummaryProjections";
import { LabelLLM } from "./components/LabelLLM";
import { useFlags } from "@/utils/flagsmith/useFlags";
import { useRef, useState, useEffect } from "react";
import {
    useFinanceLab,
    useFinanceLabAboutCompany,
} from "@/entities/FinanceLab";

export default function InvestmentMemo({
    opportunityId,
}: {
    opportunityId?: string;
}) {
    const company = useApiQuery(getCompany);
    const { startGenerateMetrics, isPolling } = useFinanceLab();
    const sfdcData = useApiQuery(getSFDCData);
    const [rightDrawerOpen] = useAtom(rightDrawerOpenAtom);
    const selectedMetricForDrawer = useAtomValue(selectedMetricForDrawerAtom);
    const selectedMetricForLineage = useAtomValue(selectedMetricForLineageAtom);
    const showInvestmentCriteriaDrawer = useAtomValue(
        showInvestmentCriteriaDrawerAtom,
    );
    const flags = useFlags();
    const showComingSoon = flags.enabled("show-coming-soon-placeholders");
    const showM05 = flags.enabled("show-im-m05");
    const { text, isGenerating, generateMemoText } = useMemoText();
    const {
        aboutText,
        generationBuffer: aboutBuffer,
        isLoading: isLoadingAbout,
        clearBuffer,
    } = useFinanceLabAboutCompany();
    const [showCompanyName, setShowCompanyName] = useState(false);
    const overviewRef = useRef<HTMLDivElement>(null);
    const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const otherWebsites =
        text[MemoTextJsonTypeEnum.COMPANY_METADATA]?.data.otherWebsites;

    const handleGenerateMetrics = () => {
        if (company.data?.uuid) {
            startGenerateMetrics({ companyId: company.data.uuid });
            generateMemoText(company.data.website);
        }
    };

    useEffect(() => {
        const observer = new IntersectionObserver(
            ([entry]) => {
                if (timeoutRef.current) {
                    clearTimeout(timeoutRef.current);
                }

                timeoutRef.current = setTimeout(() => {
                    setShowCompanyName(!entry.isIntersecting);
                }, 800);
            },
            {
                threshold: 0,
                rootMargin: "-150px 0px 0px 0px",
            },
        );

        if (overviewRef.current) {
            observer.observe(overviewRef.current);
        }

        return () => {
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
            }
            observer.disconnect();
        };
    }, []);

    if (!company.data) {
        return <Spinner fullScreen />;
    }

    return (
        <>
            <DataSourcePanel />
            <div
                className={clsx(
                    "flex-1",
                    "mt-8 pt-16 sm:pt-8 lg:mt-0 lg:pt-0",
                    "flex flex-col items-center justify-center",
                )}
            >
                <motion.div
                    initial={false}
                    animate={{
                        paddingRight: rightDrawerOpen ? 24 : 0,
                        paddingLeft: rightDrawerOpen ? 24 : 0,
                    }}
                    exit={{
                        paddingRight: rightDrawerOpen ? 24 : 0,
                        paddingLeft: rightDrawerOpen ? 24 : 0,
                    }}
                    transition={{
                        ...SLIDE_TRANSITION,
                        paddingRight: {
                            duration: ANIMATION_DURATION,
                            ease: "easeInOut",
                            delay: rightDrawerOpen ? 0 : ANIMATION_DURATION,
                        },
                        paddingLeft: {
                            duration: ANIMATION_DURATION,
                            ease: "easeInOut",
                            delay: rightDrawerOpen ? 0 : ANIMATION_DURATION,
                        },
                    }}
                    className="mx-auto w-full max-w-[800px]"
                >
                    <div
                        className={clsx(
                            "hidden sm:block",
                            "sticky top-0 z-10",
                            "px-4 sm:px-6 lg:px-12 xl:px-0",
                        )}
                    >
                        <div className="mx-auto">
                            <div
                                className={clsx(
                                    "bg-default py-4 sm:pb-4 sm:pt-8",
                                    "relative flex flex-col gap-2 sm:flex-row sm:justify-between sm:gap-0",
                                )}
                            >
                                <div
                                    className={clsx(
                                        "flex flex-col",
                                        "max-w-[400px] justify-center gap-2",
                                    )}
                                >
                                    <Breadcrumbs
                                        pages={[
                                            {
                                                name: "Legacy Admin",
                                                href: "/",
                                            },
                                            {
                                                name: company.data.name,
                                                href: `/beta/companies/${company.data.uuid}`,
                                                truncate: true,
                                            },
                                            { name: "Investor Memo" },
                                        ]}
                                    />
                                    <div
                                        className={clsx(
                                            "bg-default absolute left-0 top-full w-full",
                                            "px-4 transition-all duration-300 ease-in-out sm:px-6 lg:px-0",
                                            showCompanyName
                                                ? "max-h-60 py-4 opacity-100"
                                                : "max-h-0 overflow-hidden opacity-0",
                                        )}
                                    >
                                        <div className="flex flex-col gap-2">
                                            <div className="flex items-center gap-4">
                                                <DynamicLogo className="size-8 rounded-lg" />
                                                <h2 className="display-s">
                                                    {company.data.name}
                                                </h2>
                                            </div>
                                            <MetaInfoBar
                                                company={company.data}
                                                sfdcData={sfdcData.data}
                                                text={text}
                                                isGenerating={false}
                                                isLoadingCompany={
                                                    company.isLoading
                                                }
                                                isLoadingSfdc={
                                                    sfdcData.isLoading
                                                }
                                                hideDataSources={true}
                                            />
                                        </div>
                                    </div>
                                </div>
                                <div className="flex flex-row gap-2">
                                    <button
                                        className={clsx(
                                            "secondary flex min-w-0 items-center gap-2",
                                        )}
                                        onClick={handleGenerateMetrics}
                                        disabled={
                                            isPolling || !company.data?.uuid
                                        }
                                    >
                                        {isPolling ? (
                                            <Spinner className="shrink-0" />
                                        ) : (
                                            <>
                                                <ArrowPathIcon
                                                    className="size-4"
                                                    strokeWidth={2}
                                                />{" "}
                                                {!rightDrawerOpen && (
                                                    <span>Refresh Metrics</span>
                                                )}
                                            </>
                                        )}
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className={clsx("flex-1 overflow-y-auto")}>
                        <div
                            className={clsx(
                                "mx-auto px-6 py-4",
                                "sm:py-8 lg:px-12 xl:px-0",
                            )}
                        >
                            <div id="company-overview" ref={overviewRef}>
                                <div
                                    className={clsx(
                                        "flex flex-row",
                                        "mb-6 items-center gap-6 sm:mb-0",
                                        "sm:block",
                                    )}
                                >
                                    <DynamicLogo />
                                    <PageHeading />
                                </div>
                                <div className="mb-4 sm:mb-6">
                                    <MetaInfoBar
                                        company={company.data}
                                        sfdcData={sfdcData.data}
                                        text={text}
                                        isGenerating={isGenerating}
                                        isLoadingCompany={company.isLoading}
                                        isLoadingSfdc={sfdcData.isLoading}
                                    />
                                </div>
                            </div>
                            <MemoCollapse
                                id="about-company"
                                title="About Company"
                                defaultOpen={true}
                            >
                                <div className="mb-4 w-full sm:mb-6">
                                    {!aboutBuffer && !isLoadingAbout && (
                                        <ExpandableMarkdown
                                            text={aboutText}
                                            maxLength={1800}
                                        />
                                    )}
                                    {isLoadingAbout && (
                                        <div className="flex w-full flex-col gap-4">
                                            {[1, 2, 3].map((i) => (
                                                <div
                                                    key={i}
                                                    className={clsx(
                                                        "h-6 w-full bg-slate-700",
                                                        "animate-pulse rounded",
                                                    )}
                                                />
                                            ))}
                                        </div>
                                    )}
                                    {aboutBuffer && !isLoadingAbout && (
                                        <p>
                                            <TypewriterEffect
                                                text={aboutBuffer}
                                                className="body-s"
                                                onComplete={() => {
                                                    clearBuffer();
                                                }}
                                            />
                                        </p>
                                    )}
                                </div>
                                <div
                                    className={clsx(
                                        "mb-6",
                                        "flex flex-wrap",
                                        "gap-2",
                                        "sm:mb-10",
                                    )}
                                >
                                    <LabelLLM />
                                    {[
                                        company.data.website,
                                        ...(otherWebsites || []),
                                    ]
                                        .filter(
                                            (href): href is string => !!href,
                                        )
                                        .map((href) => (
                                            <Link
                                                href={href}
                                                title={formattedUrl(href)}
                                                key={href}
                                            />
                                        ))}
                                </div>
                                <General opportunityId={opportunityId} />
                            </MemoCollapse>
                            <MemoCollapse
                                id="transaction-summary"
                                title="Transaction summary"
                                defaultOpen={true}
                            >
                                <TransactionSummary />
                            </MemoCollapse>
                            <MemoCollapse
                                id="deal-structure"
                                title="Deal Structure"
                                defaultOpen={true}
                            >
                                <DealStructure opportunityId={opportunityId} />
                            </MemoCollapse>
                            <MemoCollapse
                                id="financial-highlights"
                                title="Financial Highlights"
                                defaultOpen={true}
                            >
                                <FinancialHighlights />
                            </MemoCollapse>
                            <MemoCollapse
                                id="investment-rationale"
                                title="Investment Rationale"
                                defaultOpen={true}
                            >
                                <InvestmentRationale />
                            </MemoCollapse>
                            <MemoCollapse
                                id="due-diligence"
                                title="Due Diligence"
                                defaultOpen={true}
                            >
                                <DueDiligence
                                    companyId={company.data.uuid}
                                    opportunityId={opportunityId}
                                />
                            </MemoCollapse>
                            {showComingSoon && (
                                <>
                                    <MemoCollapse
                                        id="key-risks"
                                        title="Key Risks"
                                        defaultOpen={true}
                                    >
                                        <KeyRisks />
                                    </MemoCollapse>
                                    <MemoCollapse
                                        id="funding-and-valuation"
                                        title="Funding and Valuation"
                                        defaultOpen={true}
                                    >
                                        <FundingAndValuation />
                                    </MemoCollapse>
                                </>
                            )}
                            <MemoCollapse
                                id="industry-overview"
                                title="Industry & Value Chain Analysis"
                                defaultOpen={true}
                            >
                                <IndustryOverview />
                            </MemoCollapse>
                            {showM05 && (
                                <>
                                    <MemoCollapse
                                        id="summary-financials"
                                        title="Summary Financials"
                                        defaultOpen={true}
                                    >
                                        <SummaryFinancials />
                                    </MemoCollapse>
                                    <MemoCollapse
                                        id="summary-projections"
                                        title="Summary Projections"
                                        defaultOpen={true}
                                    >
                                        <SummaryProjections />
                                    </MemoCollapse>
                                </>
                            )}
                        </div>
                    </div>
                </motion.div>
            </div>
            <RightDrawerContent size={RightDrawerSize.EXPANDED}>
                {showInvestmentCriteriaDrawer ? (
                    <InvestmentCriteriaDrawer />
                ) : selectedMetricForLineage ? (
                    <LineagePanel />
                ) : selectedMetricForDrawer ? (
                    <FinanceLabMetricTable
                        metric={selectedMetricForDrawer.metric}
                        title={selectedMetricForDrawer.title}
                    />
                ) : null}
            </RightDrawerContent>
        </>
    );
}
